import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getModule } from "@/services/admin/modules.service";
import { Module } from "@/services/admin/modules.interfaces";
import { useAppToast } from "@/hooks/useAppToast";
import { ModuleTestPoolExerciseProvider } from "./ModuleTestPoolExerciseContext";
import { ModuleTestPoolExercises } from "./ModuleTestPoolExercises";
import { useModuleTestPoolExerciseContext } from "./ModuleTestPoolExerciseContext";

const RECOMMENDED_POOL_SIZE = 10;

const PoolSizeWarning: React.FC = () => {
  const { state } = useModuleTestPoolExerciseContext();
  if (!state.poolSizeBelowRecommended || state.exercises.length === 0) return null;
  return (
    <div className="flex items-center gap-2 p-3 mb-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
      <AlertTriangle className="w-5 h-5 shrink-0" />
      <p className="text-sm">
        O pool tem menos de {RECOMMENDED_POOL_SIZE} exercícios. Recomenda-se pelo menos {RECOMMENDED_POOL_SIZE} para o teste do módulo.
      </p>
    </div>
  );
};

const EditModuleTestPoolPageContent: React.FC = () => (
  <>
    <PoolSizeWarning />
    <ModuleTestPoolExercises />
  </>
);

const EditModuleTestPoolPage: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const toast = useAppToast();
  const [moduleData, setModuleData] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!moduleId) return;
    let cancelled = false;
    setLoading(true);
    getModule(moduleId)
      .then((data) => {
        if (!cancelled) setModuleData(data);
      })
      .catch(() => {
        if (!cancelled) toast.error("Módulo não encontrado.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [moduleId, toast]);

  if (loading) {
    return (
      <AdminPageLayout title="Carregando...">
        <div className="py-8 text-muted-foreground">Carregando...</div>
      </AdminPageLayout>
    );
  }

  if (!moduleId || !moduleData) {
    return (
      <AdminPageLayout title="Módulo não encontrado">
        <div className="py-8 text-muted-foreground">Módulo não encontrado.</div>
        <Link to="/admin/educational/module-tests" className="text-primary hover:underline">
          Voltar para Testes de módulo
        </Link>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title={`Pool do teste: ${moduleData.name}`}>
      <div className="mb-4">
        <Link
          to="/admin/educational/module-tests"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar para Testes de módulo
        </Link>
      </div>
      <main className="space-y-6">
        <p className="text-muted-foreground">
          Exercícios deste pool podem ser sorteados no teste do módulo. Crie, edite, remova e reordene por arrastar.
        </p>
        <ModuleTestPoolExerciseProvider moduleId={moduleId}>
          <EditModuleTestPoolPageContent />
        </ModuleTestPoolExerciseProvider>
      </main>
    </AdminPageLayout>
  );
};

export default EditModuleTestPoolPage;
